import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BiGrid,
  BiCalendar,
  BiUser,
  BiPackage,
  BiGroup,
  BiSend,
  BiCartAlt,
  BiStore,
  BiNotepad,
  BiChart,
  BiMoney,
  BiStats,
  BiReceipt,
  BiChevronDown,
  BiChevronUp,
  BiLineChart,
  BiWallet,
  BiCreditCard,
} from "react-icons/bi";
import "../../assets/css/Sidebar.css";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";

const Sidebar = ({ collapsed, toggleSidebar, activeMenu, setActiveMenu }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [notificationCounts, setNotificationCounts] = useState({
    chit: 0,
    customer: 0,
    expense: 0,
    loan: 0,
    orders: 0,
    product: 0,
    purchase_details: 0,
    retailer_details: 0,
    sales: 0,
    stock: 0,
    supplier: 0,
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Decode JWT Token
  const decodeToken = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return {};
    }
  };

  // Get role safely
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role") ||
    (token ? decodeToken(token)?.role : null) ||
    "Employee";

  // Fetch notifications every 5 seconds
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const employeeId = localStorage.getItem("username") || "";
        const apiUrl =
          role === "Employee"
            ? `${ApiUrl}/Api/siderbar_notification.php?employe_id=${employeeId}`
            : `${ApiUrl}/Api/siderbar_notification.php`;

        const response = await axios.get(apiUrl);
        const data = response.data;

        if (data.status) {
          let counts = {
            chit: 0,
            customer: 0,
            expense: 0,
            loan: 0,
            orders: 0,
            product: 0,
            purchase_details: 0,
            retailer_details: 0,
            sales: 0,
            stock: 0,
            supplier: 0,
          };

          if (role === "Employee" && Array.isArray(data.data)) {
            data.data.forEach((item) => {
              if (
                item.viewed === "false" &&
                counts.hasOwnProperty(item.table_name)
              ) {
                counts[item.table_name]++;
              }
            });
          } else if (role === "Admin" && data.counts) {
            Object.keys(counts).forEach((key) => {
              counts[key] = Number(data.counts[key] || 0);
            });
          }

          setNotificationCounts(counts);
        }
      } catch (error) {
        console.error("Notification fetch error:", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [role]);

  // Update active menu from URL
  useEffect(() => {
    const path = location.pathname.substring(1);
    if (path.includes("/")) {
      const [parent, child] = path.split("/");
      setActiveMenu(child || parent);
      setOpenDropdown(parent);
    } else {
      setActiveMenu(path || "dashboard");
      setOpenDropdown(null);
    }
  }, [location.pathname, setActiveMenu]);

  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const handleItemClick = (item) => {
    if (item.hasDropdown) {
      toggleDropdown(item.id);
      if (collapsed) toggleSidebar();
    } else {
      setActiveMenu(item.id);
      navigate(`/${item.id}`);
    }
  };

  const handleSubItemClick = (parentId, subItem) => {
    setActiveMenu(subItem.id);
    navigate(`/${parentId}/${subItem.id}`);
  };

  // Menu Items - "Purchases" only for Admin
  const menuItems = [
    { id: "dashboard", icon: <BiGrid />, label: "Dashboard" },
    { id: "daily-rates", icon: <BiCalendar />, label: "Daily Rates" },
    ...(role === "Admin"
      ? [
        {
          id: "user",
          icon: <BiUser />,
          label: "User"
        },
      ]
      : []),

    {
      id: "products",
      icon: <BiPackage />,
      label: "Products",
      showBadge: notificationCounts.product > 0,
      badgeValue: notificationCounts.product,
    },
    {
      id: "customers",
      icon: <BiGroup />,
      label: "Customers",
      showBadge: notificationCounts.customer > 0,
      badgeValue: notificationCounts.customer,
    },
    {
      id: "suppliers",
      icon: <BiSend />,
      label: "Suppliers",
      showBadge: notificationCounts.supplier > 0,
      badgeValue: notificationCounts.supplier,
    },

    // Only show Purchases for Admin
    ...(role === "Admin"
      ? [
        {
          id: "purchases",
          icon: <BiCartAlt />,
          label: "Purchases",
          showBadge: notificationCounts.purchase_details > 0,
          badgeValue: notificationCounts.purchase_details,
        },
      ]
      : []),

    {
      id: "retailers",
      icon: <BiStore />,
      label: "Retailers",
      showBadge: notificationCounts.retailer_details > 0,
      badgeValue: notificationCounts.retailer_details,
    },
    {
      id: "orders",
      icon: <BiNotepad />,
      label: "Orders",
      showBadge: notificationCounts.orders > 0,
      badgeValue: notificationCounts.orders,
    },
    {
      id: "sales",
      icon: <BiLineChart />,
      label: "Sales",
      showBadge: notificationCounts.sales > 0,
      badgeValue: notificationCounts.sales,
    },
    {
      id: "balance",
      icon: <BiWallet />,
      label: "Balance",
      hasDropdown: true,
      subItems: [
        { id: "white-page-balance", label: "White Sheet" },
        { id: "blue-page-balance", label: "Blue Sheet" },
      ],
    },
    {
      id: "chit/chit-list",
      icon: <BiCreditCard />,
      label: "Chit",
      hasDropdown: false,
      showBadge: notificationCounts.chit > 0,
      badgeValue: notificationCounts.chit,
    },
    {
      id: "loan",
      icon: <BiMoney />,
      label: "Loan",
      showBadge: notificationCounts.loan > 0,
      badgeValue: notificationCounts.loan,
    },
    {
      id: "stocks",
      icon: <BiStats />,
      label: "Stocks",
      showBadge: notificationCounts.stock > 0,
      badgeValue: notificationCounts.stock,
    },
    {
      id: "expense",
      icon: <BiReceipt />,
      label: "Expense",
      showBadge: notificationCounts.expense > 0,
      badgeValue: notificationCounts.expense,
    },
    ...(role === "Admin"
      ? [
        {
          id: "sales-vs-expense",
          icon: <BiChart />,
          label: "Sales Vs Expense"
        },
      ]
      : []),

    ...(role === "Admin"
      ? [
        {
          id: "Report",
          icon: <BiNotepad />,
          label: "Reports"
        },
      ]
      : []),


  ];

  return (
    <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="logo-container" onClick={toggleSidebar}>
        {collapsed ? (
          <img
            src={require("../../assets/images/Final logo.png")}
            alt="Tirupur Jewellery"
            className="logo-icon"
          />
        ) : (
          <div className="logo-with-text">
            <img
              src={require("../../assets/images/Final logo.png")}
              alt="Tirupur Jewellery"
              className="logo-full"
            />
          </div>
        )}
      </div>

      <div className="menu-container">
        {menuItems.map((item) => (
          <div key={item.id}>
            <div
              className={`menu-item ${activeMenu === item.id ||
                (item.hasDropdown &&
                  item.subItems?.some((sub) => sub.id === activeMenu))
                ? "active"
                : ""
                } ${item.hasDropdown ? "has-dropdown" : ""}`}
              onClick={() => handleItemClick(item)}
            >
              <div className="icon">{item.icon}</div>
              {!collapsed && (
                <>
                  <span className="label">{item.label}</span>
                  {item.showBadge && (
                    <div className="badge-circle">
                      <span>{item.badgeValue}</span>
                    </div>
                  )}
                  {item.hasDropdown && (
                    <span className="dropdown-arrow">
                      {openDropdown === item.id ? <BiChevronUp /> : <BiChevronDown />}
                    </span>
                  )}
                </>
              )}
              {collapsed && item.showBadge && (
                <div className="badge-circle badge-collapsed">
                  <span>{item.badgeValue}</span>
                </div>
              )}
            </div>

            {/* Submenu */}
            {item.hasDropdown && openDropdown === item.id && !collapsed && (
              <div className="submenu">
                {item.subItems.map((subItem) => (
                  <div
                    key={subItem.id}
                    className={`submenu-item ${activeMenu === subItem.id ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubItemClick(item.id, subItem);
                    }}
                  >
                    <span className="submenu-label">{subItem.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;